<template>
    <div class="w-100">
        <b-container class="p-0" fluid>
            <b-row>
                <b-col>
                    <h2>{{ $t("contacts.lists.wizard.select-contacts-title") }}</h2>
                </b-col>
            </b-row>
        </b-container>

        <div class="mb-2">
            <b-row align-h="between">
                <b-col class="d-flex align-items-center" cols="12" md="auto">
                    <div class="input-icon-wrapper search">
                        <div class="saysimple-icon embeded-icon search position-absolute text-secondary" />
                        <b-form-input
                            id="search-query"
                            v-model="searchQuery"
                            :placeholder="$t('paginator.search')"
                            class="bg-light"
                        />
                    </div>

                    <contact-filters v-model="filters" />

                    <b-dropdown
                        v-model="perPage"
                        v-b-tooltip.hover.bottom="$t('paginator.number-of-items-per-page')"
                        :text="`${$t('paginator.entries-capitalized')}: ${perPage}`"
                        class="per-page-selector ml-1 round-medium"
                        no-caret
                        variant="light"
                    >
                        <b-dropdown-item
                            v-for="perPageOption in perPageOptions"
                            :key="perPageOption"
                            @click="perPage = perPageOption"
                        >
                            {{ perPageOption }}
                        </b-dropdown-item>
                    </b-dropdown>
                </b-col>

                <b-col cols="auto">
                    <span class="ml-auto no-wrap">{{ $tc("contacts.lists.contacts-selected", totalSelected) }}</span>
                    <b-button :disabled="isSelectingAll" class="mx-1" variant="light" @click="deselectAllContacts"
                        >{{ $t("contacts.lists.wizard.select-none") }}
                    </b-button>
                    <b-button
                        :disabled="isSelectingAll || count === 0"
                        class="no-wrap"
                        variant="primary"
                        @click="selectAllContacts"
                    >
                        <b-spinner v-if="isSelectingAll" small />
                        {{ $tc("contacts.lists.wizard.select-n-contacts", count) }}
                    </b-button>
                </b-col>
            </b-row>
        </div>

        <div v-if="isLoading" class="d-flex justify-content-center mb-3">
            <b-spinner />
        </div>

        <b-table
            v-show="!isLoading"
            ref="contactsTable"
            :fields="tableFields"
            :items="contacts"
            :no-border-collapse="false"
            :no-sort-reset="true"
            no-select-on-click
            responsive
            select-mode="multi"
            selectable
            sticky-header="400px"
            tbody-tr-class="no-wrap b-table-row-narrow"
            @row-clicked="clickedRow"
        >
            <template #head(selected)="data">
                <b-form-checkbox
                    v-model="selectedAll"
                    :indeterminate="indeterminate"
                    @change="toggleContactsCurrentPage()"
                />
            </template>

            <template #cell(selected)="data">
                <b-form-checkbox v-model="data.rowSelected" @change="clickedRow(data.item, data.index)" />
            </template>

            <template #cell(labels)="data">
                <b-badge
                    v-for="label in data.item.labels"
                    :key="label.id"
                    :style="styleByColor(label.color)"
                    class="mr-50 badge-margin"
                >
                    {{ label.name }}
                </b-badge>
            </template>
        </b-table>
        <div v-if="!isLoading" class="mx-2 mb-2 mt-4">
            <b-row>
                <b-col
                    class="d-flex align-items-center justify-content-center justify-content-sm-start"
                    cols="12"
                    sm="6"
                >
                    <span class="text-muted">
                        {{ $t("paginator.showing") }} {{ dataMeta.to }} {{ $t("paginator.of") }} {{ dataMeta.of }}
                        {{ $t("paginator.entries") }}
                    </span>
                </b-col>

                <b-col class="d-flex align-items-center justify-content-center justify-content-sm-end" cols="12" sm="6">
                    <b-pagination
                        v-model="page"
                        :per-page="perPage"
                        :total-rows="count"
                        class="mb-0 mt-1 mt-sm-0"
                        first-number
                        last-number
                        next-class="next-item"
                        prev-class="prev-item"
                        size="sm"
                    >
                        <template #prev-text>
                            <font-awesome-icon icon="chevron-left" size="lg" />
                        </template>
                        <template #next-text>
                            <font-awesome-icon icon="chevron-right" size="lg" />
                        </template>
                    </b-pagination>
                </b-col>
            </b-row>
        </div>
    </div>
</template>

<script>
import useContactLabels from "@/composables/v2.1/useContactLabels";
import ToastificationContent from "@/layouts/components/toastification/toastificationContent.vue";
import i18n from "@/libs/i18n";
import { styleByColor } from "@/utils/color";
import { resolveToPhoneNumber } from "@/utils/phoneNumber";
import useContacts from "@/views/modules/contacts/useContacts";
import useFilters from "@/views/modules/contacts/useFilters";
import { computed, nextTick, ref, watch } from "@vue/composition-api";
import {
    BBadge,
    BButton,
    BCol,
    BContainer,
    BDropdown,
    BDropdownForm,
    BDropdownItem,
    BFormCheckbox,
    BFormCheckboxGroup,
    BFormGroup,
    BFormInput,
    BListGroup,
    BListGroupItem,
    BPagination,
    BRow,
    BSpinner,
    BTable,
    VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { useToast } from "vue-toastification/composition";
import ContactFilters from "./contactFilters.vue";
import { clone } from "lodash";

export default {
    components: {
        BContainer,
        BRow,
        BCol,
        BTable,
        BButton,
        BFormInput,
        BSpinner,
        BPagination,
        BDropdown,
        BDropdownItem,
        BDropdownForm,
        BListGroup,
        BListGroupItem,
        BFormGroup,
        BFormCheckboxGroup,
        BFormCheckbox,
        BBadge,
        vSelect,
        ContactFilters,
    },
    directives: {
        "b-tooltip": VBTooltip,
    },
    props: {
        selectedContacts: {
            type: Array,
            default() {
                return [];
            },
        },
    },
    setup(props, context) {
        const { page, perPage, isSortDirDesc, sortBy, searchQuery, perPageOptions } = useFilters();
        const currentSelected = ref([]);
        const contactsTable = ref(null);
        const isSelectingAll = ref(false);
        const selectedAll = ref(false);
        const indeterminate = ref(false);
        const toast = useToast();
        const { count, contacts, isLoading, fetchContacts } = useContacts();

        const localContacts = ref(clone(contacts));

        watch(contacts, (nValue) => {
            localContacts.value = filterContacts(nValue);
        });

        /**
         * LABELS
         */
        const { contactLabels, fetchContactLabels } = useContactLabels();
        fetchContactLabels({ params: { fields: "id,name,color" } });

        const fields =
            "name,email,phone,street,houseNumber,zipcode,city,province,countryCode,reference,company,jobTitle";

        const search = computed(() => ({
            "labels.id": {
                $in: filters.value.labels.length ? filters.value.labels : undefined,
            },
            $or: [
                { name: { $cont: searchQuery.value } },
                {
                    phone: {
                        $cont: resolveToPhoneNumber(searchQuery.value) ?? searchQuery.value,
                    },
                },
                { email: { $cont: searchQuery.value } },
                { company: { $cont: searchQuery.value } },
                { reference: { $cont: searchQuery.value } },
            ],
        }));

        const tableFields = [
            { key: "selected", label: "" },
            { key: "name", label: i18n.t("entities.contact.Name") },
            { key: "email", label: i18n.t("entities.contact.Email") },
            { key: "phone", label: i18n.t("entities.contact.Phone") },
            { key: "street", label: i18n.t("entities.contact.Street") },
            { key: "houseNumber", label: i18n.t("entities.contact.House number") },
            { key: "zipcode", label: i18n.t("entities.contact.Zip code") },
            { key: "city", label: i18n.t("entities.contact.City") },
            { key: "province", label: i18n.t("entities.contact.Province") },
            { key: "countryCode", label: i18n.t("entities.contact.Country") },
            { key: "reference", label: i18n.t("entities.contact.Reference") },
            { key: "company", label: i18n.t("entities.contact.Company") },
            { key: "jobTitle", label: i18n.t("entities.contact.Job title") },
            { key: "labels", label: i18n.t("settings.labels.Labels") },
        ];

        // Temporarily save them like this so the selections show up right.
        currentSelected.value = props.selectedContacts.map((contactId) => ({ id: contactId }));
        // Then fetch the entire objects (we can't assume they'll be fetched anyway because we paginate)
        if (currentSelected.value.length > 0) {
            fetchContacts(
                {
                    fields,
                    s: {
                        $and: [
                            {
                                id: {
                                    $in: props.selectedContacts,
                                },
                            },
                        ],
                    },
                },
                (contacts) => {
                    currentSelected.value = contacts.data;
                }
            );
        }

        const filters = ref({
            labels: [],
            filterOption: "or", // Default filter behaviour is show results with at least one selected label
        });

        const params = computed(() => ({
            fields,
            s: search.value,
            join: "labels",
            limit: perPage.value,
            page: page.value,
            sort: `${sortBy.value},${isSortDirDesc.value === true ? "DESC" : "ASC"}`,
        }));

        const dataMeta = computed(() => {
            const localItemsCount = contactsTable.value ? contactsTable.value.localItems.length : 0;

            return {
                from: perPage.value * (page.value - 1) + (localItemsCount ? 1 : 0),
                to: perPage.value * (page.value - 1) + localItemsCount,
                of: count.value,
            };
        });

        const localFetchContacts = () => {
            fetchContacts(params.value, (result) => {
                contacts.value = result.data.data;
                count.value = result.data.total;

                // Handle displaying selected contacts as selected
                if (currentSelected.value?.length > 0) {
                    // NOTE: nextTick because else it will perform this while the old records are still there
                    nextTick(() => {
                        currentSelected.value.forEach((selectedContact) => {
                            const tableContactIndex = contactsTable.value.localItems.findIndex(
                                (contact) => contact.id === selectedContact.id
                            );
                            if (~tableContactIndex) {
                                contactsTable.value.selectRow(tableContactIndex);
                            }
                        });
                    });
                }
            });
        };

        const deselectAllContacts = () => {
            currentSelected.value = [];
            contactsTable.value.clearSelected();
            context.emit("update-selection", currentSelected.value);
        };

        const selectAllContacts = () => {
            isSelectingAll.value = true;
            fetchContacts(
                {
                    fields,
                    s: search.value,
                    join: "labels",
                },
                (contacts) => {
                    currentSelected.value = filterContacts(contacts.data);
                    context.emit("update-selection", currentSelected.value);
                    contactsTable.value.selectAllRows();
                }
            )
                .catch(() => {
                    toast({
                        component: ToastificationContent,
                        props: {
                            title: i18n.t("contacts.Contacts"),
                            text: i18n.t("contacts.lists.errors.couldnt-select-all"),
                            icon: "times",
                            variant: "danger",
                        },
                    });
                })
                .finally(() => {
                    isSelectingAll.value = false;
                });
        };

        const toggleContactsCurrentPage = () => {
            const selectedContactIdsCurrentPage = selectedIdsCurrentPage();

            if (
                selectedContactIdsCurrentPage.length === 0 ||
                selectedContactIdsCurrentPage.length < localContacts.value
            ) {
                currentSelected.value = [...currentSelected.value, ...localContacts.value];
                context.emit("update-selection", currentSelected.value);
                contactsTable.value.selectAllRows();
            } else {
                currentSelected.value = currentSelected.value.filter(
                    (contact) => !selectedContactIdsCurrentPage.includes(contact.id)
                );
                context.emit("update-selection", currentSelected.value);
                contactsTable.value.clearSelected();
            }
        };

        const clickedRow = (clickedContact, index) => {
            const isSelected = contactsTable.value.isRowSelected(index);

            if (isSelected) {
                contactsTable.value.unselectRow(index);

                currentSelected.value = currentSelected.value.filter((contact) => contact.id !== clickedContact.id);
            } else {
                contactsTable.value.selectRow(index);

                // Only add the contact if it isn't in the array already
                if (!currentSelected.value.find((contact) => contact.id === clickedContact.id)) {
                    currentSelected.value = [...currentSelected.value, clickedContact];
                }
            }
            context.emit("update-selection", currentSelected.value);
        };

        const selectedIdsCurrentPage = () => {
            const currentSelectedIds = currentSelected.value.map((contact) => contact.id);

            return localContacts.value.reduce((list, contact) => {
                if (currentSelectedIds.includes(contact.id)) {
                    list.push(contact.id);
                }
                return list;
            }, []);
        };

        const refetchContacts = () => {
            if (page.value === 1) {
                localFetchContacts();
            }
            // There is a watcher on page so localFetchContacts will be ran automatically
            page.value = 1;
        };

        watch(filters, () => refetchContacts(), { deep: true });

        watch([currentSelected, isLoading, filters], () => {
            if (isLoading.value) {
                return;
            }

            const selectedContactIdsCurrentPage = selectedIdsCurrentPage();

            if (selectedContactIdsCurrentPage.length === 0) {
                indeterminate.value = false;
                selectedAll.value = false;
            } else {
                const isAllSelected = selectedContactIdsCurrentPage.length === localContacts.value.length;
                indeterminate.value = !isAllSelected;
                selectedAll.value = isAllSelected;
            }
        });

        watch([page, perPage, sortBy], () => {
            localFetchContacts();
        });
        const queryTimeout = ref(null);
        watch(searchQuery, (query) => {
            if (query.length === 1) {
                return;
            }

            if (queryTimeout.value) {
                clearTimeout(queryTimeout.value);
            }

            queryTimeout.value = setTimeout(() => {
                localFetchContacts();
            }, 500);
        });

        const filterContacts = (nValue) => {
            // Default filter behaviour is built into the request that retrieves contacts so there is no action necessary here
            if (!filters.value.labels.length || filters.value.filterOption === "or") {
                return nValue;
            }

            const filteredContacts = nValue.filter((contact) =>
                filters.value.labels.every((labelId) =>
                    Boolean(contact.labels.find((contactLabel) => contactLabel.id === labelId))
                )
            );

            count.value = filteredContacts.count; // For pagination
            return filteredContacts;
        };

        localFetchContacts();

        return {
            tableFields,
            contacts: localContacts,
            contactLabels,
            currentSelected,
            totalSelected: computed(() => currentSelected.value.length),
            contactsTable,
            isLoading,
            searchQuery,
            dataMeta,
            page,
            perPage,
            count,
            perPageOptions,
            isSelectingAll,
            filters,
            selectedAll,
            indeterminate,
            clickedRow,
            selectAllContacts,
            deselectAllContacts,
            refetchContacts,
            styleByColor,
            toggleContactsCurrentPage,
        };
    },
};
</script>

<style>
.tabs.labels-tab .nav-tabs {
    display: block;
}

.labels-item {
    width: max-content;
}
</style>
